import { useCallback, useState } from "react";
import type { BedType } from "~/lib/api";
import { Button } from "~/components/ui/button";
import { Icon } from "~/components/ui/icons/icon";
import { Input } from "~/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "~/components/ui/select";
import { useInlineEditor } from "~/hooks/useInlineEditor";
import { BED_TYPE_LABELS } from "./accommodationLabels";

interface BedTypeEditorProps {
  unusedTypes: BedType[];
  onAdd: (type: BedType, count: number) => void;
}

export function BedTypeEditor({ unusedTypes, onAdd }: BedTypeEditorProps) {
  const [portalContainer, setPortalContainer] = useState<HTMLDivElement | null>(null);
  const containerCallbackRef = useCallback((node: HTMLDivElement | null) => {
    setPortalContainer(node);
  }, []);
  const {
    editing,
    saving,
    editState,
    containerRef,
    primaryInputRef,
    startEditing,
    save,
    handleKeyDown,
    handleBlur,
    setField,
  } = useInlineEditor<{ type: BedType; count: number }>({
    value: null,
    onSave: (entry) => {
      if (entry) onAdd(entry.type, entry.count);
    },
    toEditState: () => ({ type: unusedTypes[0], count: "1" }),
    fromEditState: (state) => {
      const count = parseInt(state.count, 10);
      if (!state.type || isNaN(count) || count <= 0) return null;
      return { type: state.type as BedType, count };
    },
  });

  const setContainerRef = useCallback(
    (node: HTMLDivElement | null) => {
      containerRef.current = node;
      containerCallbackRef(node);
    },
    [containerCallbackRef, containerRef],
  );

  if (editing) {
    return (
      <div ref={setContainerRef} className="flex gap-2 items-center" onBlur={handleBlur}>
        <Input
          ref={primaryInputRef}
          type="number"
          min="1"
          step="1"
          value={editState.count}
          onChange={(e) => setField("count", e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="1"
          disabled={saving}
          className="w-24 h-8 text-sm"
          aria-label="Count"
        />
        <Select value={editState.type} onValueChange={(v) => setField("type", v)}>
          <SelectTrigger size="sm" className="h-8 w-auto min-w-24 text-sm" aria-label="Bed type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent container={portalContainer}>
            {unusedTypes.map((type) => (
              <SelectItem key={type} value={type}>
                {capitalize(BED_TYPE_LABELS[type])}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          size="icon-sm"
          variant="ghost"
          onMouseDown={(e) => e.preventDefault()}
          onClick={save}
          disabled={saving}
          aria-label="Confirm"
        >
          <Icon i="check" className="size-6 text-primary" />
        </Button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={startEditing}
      className="text-sm text-muted-foreground hover:text-primary transition-colors cursor-pointer"
    >
      + Add beds
    </button>
  );
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
